/** @format */

import { styled } from "@mui/material/styles";
import {
    Box,
    Button,
    Chip,
    Typography,
    TableContainer,
    TableCell,
    TypographyProps,
} from "@mui/material";
import { colors } from "../../utility/types/constant";

// ─────────────────────────────────────────────────────────────────────────
// Collect / AARRP shared styles.
// Mirrors the sandbox design language: navy hero, soft white cards,
// Montserrat type, mint accent. Reused across every Collect page.
// ─────────────────────────────────────────────────────────────────────────

// ── Page wrapper ─────────────────────────────────────────────────────────
export const CollectPageWrapper = styled("div")(() => ({
    minHeight: "100vh",
    backgroundColor: "#f6f8fb",
    paddingBottom: "60px",
    marginTop: "80px"
}));

// ── Hero banner ──────────────────────────────────────────────────────────
export const CollectHeroBanner = styled("div")(({ theme }) => ({
    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.blueRibbon} 100%)`,
    padding: "56px 24px 44px",
    position: "relative",
    overflow: "hidden",
    [theme.breakpoints.down("md")]: {
        padding: "44px 20px 32px",
    },
    [theme.breakpoints.down("sm")]: {
        padding: "32px 16px 24px",
    },
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background:
            "radial-gradient(ellipse at 75% 30%, rgba(30,193,149,0.18) 0%, transparent 60%)",
        pointerEvents: "none",
    },
}));

export const CollectHeroInner = styled("div")(() => ({
    maxWidth: "1200px",
    margin: "0 auto",
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "10px",
}));

export const CollectBadge = styled(Chip)(() => ({
    backgroundColor: "rgba(30, 193, 149, 0.2)",
    color: colors.mainSecondary,
    border: `1px solid rgba(30,193,149,0.4)`,
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
    fontSize: "0.7rem",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    alignSelf: "flex-start",
}));

export const CollectHeroTitle = styled(Typography)(() => ({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "clamp(1.6rem, 3vw, 2.4rem)",
    fontWeight: 800,
    color: "#ffffff",
    letterSpacing: "-0.02em",
    lineHeight: 1.15,
}));

export const CollectHeroSubtitle = styled(Typography)(() => ({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "clamp(0.9rem, 1.6vw, 1.05rem)",
    fontWeight: 400,
    color: "rgba(255,255,255,0.78)",
    maxWidth: "780px",
    lineHeight: 1.65,
}));

export const CollectHeroActions = styled("div")(({ theme }) => ({
    marginTop: "20px",
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
        marginTop: "12px",
    },
}));

// ── Content area ─────────────────────────────────────────────────────────
export const CollectContentArea = styled("div")(({ theme }) => ({
    maxWidth: "1200px",
    margin: "-32px auto 0",
    padding: "0 24px",
    position: "relative",
    zIndex: 2,
    [theme.breakpoints.down("md")]: {
        padding: "0 16px",
        margin: "-20px auto 0",
    },
    [theme.breakpoints.down("sm")]: {
        padding: "0 12px",
        margin: "-12px auto 0",
    },
}));

// ── Section card ─────────────────────────────────────────────────────────
export const CollectSectionCard = styled("div")(({ theme }) => ({
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "28px",
    boxShadow: "0 2px 10px rgba(15, 23, 42, 0.06)",
    border: "1px solid rgba(15, 23, 42, 0.05)",
    marginBottom: "24px",
    [theme.breakpoints.down("sm")]: {
        padding: "18px 14px",
        borderRadius: "12px",
        marginBottom: "16px",
    },
}));

export const CollectSectionHeader = styled("div")(() => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "20px",
    gap: "12px",
    flexWrap: "wrap",
}));

export const CollectSectionTitle = styled(Typography)(() => ({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "0.95rem",
    fontWeight: 700,
    color: colors.primary,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
}));
CollectSectionTitle.defaultProps = { component: "div" } as Partial<TypographyProps>

export const CollectSectionSubtitle = styled(Typography)(() => ({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "0.85rem",
    color: "#6b7280",
    marginTop: "4px",
}));

// ── KPI / Stat cards ─────────────────────────────────────────────────────
export const CollectKpiCard = styled("div")<{ accent?: string }>(
    ({ theme, accent }) => ({
        backgroundColor: "#ffffff",
        borderRadius: "14px",
        padding: "20px 22px",
        boxShadow: "0 2px 10px rgba(15, 23, 42, 0.06)",
        border: "1px solid rgba(15, 23, 42, 0.05)",
        borderLeft: `4px solid ${accent || colors.mainSecondary}`,
        display: "flex",
        flexDirection: "column",
        gap: "6px",
        height: "100%",
        // Allow the card to shrink inside a Grid/flex track so long values
        // wrap instead of forcing the card wider than its column.
        minWidth: 0,
        [theme.breakpoints.down("sm")]: {
            padding: "14px 14px",
            borderRadius: "10px",
        },
    }),
);

export const CollectKpiLabel = styled(Typography)(() => ({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "0.72rem",
    fontWeight: 600,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
}));

export const CollectKpiValue = styled(Typography)(() => ({
    fontFamily: "'Montserrat', sans-serif",
    // Scale with the viewport but stay small enough that large-currency
    // amounts (e.g. CA$66,038.42) fit inside narrow multi-column cards.
    fontSize: "clamp(1.2rem, 2.4vw, 1.7rem)",
    fontWeight: 800,
    color: colors.primary,
    lineHeight: 1.15,
    // Never overflow the card — wrap long amounts onto a second line.
    minWidth: 0,
    maxWidth: "100%",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
}));

export const CollectKpiHint = styled(Typography)(() => ({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "0.78rem",
    color: "#6b7280",
}));

// ── Buttons ──────────────────────────────────────────────────────────────
export const CollectPrimaryButton = styled(Button)(() => ({
    backgroundColor: colors.mainSecondary,
    color: "#ffffff",
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
    textTransform: "none",
    padding: "10px 22px",
    borderRadius: "10px",
    fontSize: "0.9rem",
    boxShadow: "0 4px 12px rgba(30, 193, 149, 0.25)",
    "&:hover": {
        backgroundColor: "#16a37c",
        boxShadow: "0 6px 16px rgba(30, 193, 149, 0.35)",
    },
    "&.Mui-disabled": {
        backgroundColor: "rgba(30, 193, 149, 0.4)",
        color: "#ffffff",
    },
}));

export const CollectSecondaryButton = styled(Button)(() => ({
    backgroundColor: "rgba(255,255,255,0.12)",
    color: "#ffffff",
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 600,
    textTransform: "none",
    padding: "10px 22px",
    borderRadius: "10px",
    fontSize: "0.9rem",
    border: "1px solid rgba(255,255,255,0.25)",
    "&:hover": {
        backgroundColor: "rgba(255,255,255,0.2)",
        border: "1px solid rgba(255,255,255,0.4)",
    },
}));

export const CollectGhostButton = styled(Button)(() => ({
    color: colors.primary,
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 600,
    textTransform: "none",
    padding: "8px 16px",
    borderRadius: "10px",
    fontSize: "0.85rem",
    border: `1px solid ${colors.primary}20`,
    "&:hover": {
        backgroundColor: `${colors.primary}08`,
        border: `1px solid ${colors.primary}40`,
    },
}));

// ── Status chips ─────────────────────────────────────────────────────────
type InvoiceChipStatus =
    | "open"
    | "paid"
    | "overdue"
    | "partially_paid"
    | "draft"
    | "void";

const STATUS_PALETTE: Record<
    InvoiceChipStatus,
    { bg: string; color: string; border: string }
> = {
    open: {
        bg: "rgba(59, 130, 246, 0.12)",
        color: "#1e40af",
        border: "rgba(59, 130, 246, 0.3)",
    },
    paid: {
        bg: "rgba(30, 193, 149, 0.14)",
        color: "#0f7a5b",
        border: "rgba(30, 193, 149, 0.35)",
    },
    overdue: {
        bg: "rgba(220, 38, 38, 0.12)",
        color: "#b91c1c",
        border: "rgba(220, 38, 38, 0.3)",
    },
    partially_paid: {
        bg: "rgba(245, 158, 11, 0.15)",
        color: "#b45309",
        border: "rgba(245, 158, 11, 0.35)",
    },
    draft: {
        bg: "rgba(107, 114, 128, 0.15)",
        color: "#374151",
        border: "rgba(107, 114, 128, 0.35)",
    },
    void: {
        bg: "rgba(107, 114, 128, 0.1)",
        color: "#6b7280",
        border: "rgba(107, 114, 128, 0.25)",
    },
};

export const CollectStatusChip = styled(Chip, {
    shouldForwardProp: (prop) => prop !== "statusKey",
})<{ statusKey?: InvoiceChipStatus }>(
    ({ statusKey }) => {
        const palette = STATUS_PALETTE[statusKey || "open"] || STATUS_PALETTE.open;
        return {
            backgroundColor: palette.bg,
            color: palette.color,
            border: `1px solid ${palette.border}`,
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 700,
            fontSize: "0.7rem",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            height: "24px",
        };
    },
);

// ── Table styles ─────────────────────────────────────────────────────────
export const CollectTableContainer = styled(TableContainer)(() => ({
    borderRadius: "12px",
    border: "1px solid rgba(15, 23, 42, 0.06)",
    backgroundColor: "#ffffff",
    overflowX: "auto",
}));

export const CollectTableHeadCell = styled(TableCell)(() => ({
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
    fontSize: "0.72rem",
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    backgroundColor: "#f9fafb",
    borderBottom: "1px solid rgba(15, 23, 42, 0.06)",
}));

export const CollectTableBodyCell = styled(TableCell)(() => ({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "0.875rem",
    color: "#1f2937",
    borderBottom: "1px solid rgba(15, 23, 42, 0.04)",
}));

// ── Empty / loading states ───────────────────────────────────────────────
export const CollectEmptyState = styled(Box)(() => ({
    padding: "48px 24px",
    textAlign: "center",
    color: "#6b7280",
    fontFamily: "'Montserrat', sans-serif",
}));

export const CollectEmptyTitle = styled(Typography)(() => ({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "1.05rem",
    fontWeight: 700,
    color: colors.primary,
    marginBottom: "8px",
}));

export const CollectEmptyHint = styled(Typography)(() => ({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "0.875rem",
    color: "#6b7280",
    maxWidth: "440px",
    margin: "0 auto",
}));

// ── Aging buckets row ────────────────────────────────────────────────────
export const AgingBucketCard = styled("div")<{ tone?: "ok" | "warn" | "bad" }>(
    ({ tone }) => {
        const palette = {
            ok: { border: "rgba(30,193,149,0.4)", color: "#0f7a5b" },
            warn: { border: "rgba(245, 158, 11, 0.45)", color: "#b45309" },
            bad: { border: "rgba(220, 38, 38, 0.45)", color: "#b91c1c" },
        };
        const p = palette[tone || "ok"];
        return {
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            padding: "16px 18px",
            border: `1px solid ${p.border}`,
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            minHeight: "92px",
            color: p.color,
        };
    },
);

export const AgingBucketLabel = styled(Typography)(() => ({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "0.72rem",
    fontWeight: 700,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
}));

export const AgingBucketValue = styled(Typography)(() => ({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "1.25rem",
    fontWeight: 800,
}));

export const AgingBucketCount = styled(Typography)(() => ({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "0.78rem",
    color: "#6b7280",
}));

// ── Connection / state banner ────────────────────────────────────────────
export const CollectInfoBanner = styled("div")<{ tone?: "info" | "warn" | "ok" }>(
    ({ tone }) => {
        const palette = {
            info: {
                bg: "rgba(59, 130, 246, 0.08)",
                border: "rgba(59, 130, 246, 0.25)",
                color: "#1e3a8a",
            },
            warn: {
                bg: "rgba(245, 158, 11, 0.1)",
                border: "rgba(245, 158, 11, 0.3)",
                color: "#92400e",
            },
            ok: {
                bg: "rgba(30, 193, 149, 0.1)",
                border: "rgba(30, 193, 149, 0.3)",
                color: "#0f7a5b",
            },
        };
        const p = palette[tone || "info"];
        return {
            backgroundColor: p.bg,
            border: `1px solid ${p.border}`,
            color: p.color,
            borderRadius: "12px",
            padding: "14px 18px",
            fontFamily: "'Montserrat', sans-serif",
            fontSize: "0.875rem",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "20px",
        };
    },
);

// ── Helpers (utility classes) ────────────────────────────────────────────
export const FlexRow = styled("div")(() => ({
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
}));

export const FlexBetween = styled("div")(() => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
}));
